import { Injectable, inject } from '@angular/core';
import {BaseService} from '../generic/service/base.service';
import {HttpClient} from '@angular/common/http';
import {Logger} from '../generic/logging/default-log.service';
import {ConfigurationHelper} from '../generic/helper/configuration.helper';
import {AssortmentBanner} from '../components/products/banners/assortment.banner';
import {firstValueFrom} from 'rxjs';

/**
 * A service that handles the functionality needed for the Assortment Banners.
 *
 * @author Patrick van Zadel
 */
@Injectable()
export class AssortmentBannerService extends BaseService {
	private readonly http = inject(HttpClient);

	banners: AssortmentBanner[] = [];
	shownBanners: number[] = [];
	bannerIndexes: number[] = [];

	constructor() {
		const logger = inject(Logger);

		super(logger);
	}

	public getBanner() {
		return this.banners[this.getRandomBannerIndex()];
	}

	/**
	 * 1. set shown banner index in array
	 * 2. empty bannerIndexes array when all banners have been shown (shownBanners array)
	 */
	public getRandomBannerIndex(): number {
		this.resetShownBannersIfAllBannersAreShown();

		let bannerIndex =
			this.bannerIndexes[
				Math.floor(Math.random() * Math.floor(this.banners.length))
				];

		while (this.shownBanners.includes(bannerIndex)) {
			bannerIndex =
				this.bannerIndexes[
					Math.floor(Math.random() * Math.floor(this.banners.length))
					];
		}

		this.shownBanners.push(bannerIndex);

		return bannerIndex;
	}

	/**
	 * Fetches all banners from the website and add them to a local variable.
	 * @param isPlantscapingActive
	 */
	public getAssortmentBanners(isPlantscapingActive: number) {
		this.getAssortmentBannersFromBackend(isPlantscapingActive).then(
			response => {
				if (response) {
					this.banners = response;
					this.bannerIndexes = this.banners.map(
						(banner, index) => index,
					);
				}
			},
		);
	}

	public getBanners(): AssortmentBanner[] {
		return this.banners;
	}

	private resetShownBannersIfAllBannersAreShown(): void {
		if (this.shownBanners.length === this.bannerIndexes.length) {
			this.shownBanners = [];
		}
	}

	private getAssortmentBannersFromBackend(
		isPlantscapingActive: number,
	): Promise<AssortmentBanner[]> {
		const assortmentBannersUrl = ConfigurationHelper.getWebshopUrl(
			isPlantscapingActive === 1
				? '/rest/assortmentbanners/plantscaping'
				: '/rest/assortmentbanners',
		);
		return firstValueFrom(
			this.http.get<AssortmentBanner[]>(assortmentBannersUrl),
		);
	}
}
